import { useAsync } from '../lib/store';
import { api } from '../lib/api';
import type { AvatarCreditsPayload } from '../lib/types';
import { ErrorState, LoadingGrid, Panel } from '../components/ui';

/**
 * Credits.
 *
 * The brief's rule is one sentence: **do not hide attribution in source code
 * only.** So this is a real page at a real URL, it is readable without signing
 * in, and it is generated from the same rows the picker reads rather than from
 * a hand-maintained list that would drift the first time an avatar was added.
 *
 * The source's own terms are quoted verbatim rather than paraphrased. A
 * paraphrase of a licence is a second licence.
 *
 * Sprites with a named artist are listed first and by artist, because that is
 * the form the credit is actually owed in — "Sprite by X" beside one thumbnail
 * is a caption; a section under X's name listing everything of theirs is a
 * credit. Everything else is listed as officially sourced, which is the honest
 * description of a game rip: nobody individual is claimed, and inventing an
 * attribution would be worse than the true one.
 */
export function CreditsPage() {
  const state = useAsync<AvatarCreditsPayload>(() => api.get('/avatars/credits'), []);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <LoadingGrid count={3} height={160} />;

  const { sourceName, sourceUrl, sourceNotice, byArtist, entries } = state.data;
  const official = entries.filter((entry) => !entry.artistCredit);
  const unresolved = entries.filter(
    (entry) => entry.permissionStatus === 'UNVERIFIED',
  );

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Credits</h1>
          <div className="page-head__sub">
            PokeGrove is a fan project. The artwork below was made by other people.
          </div>
        </div>
      </div>

      <Panel title="Trainer Sprite Credits">
        <div className="stack">
          <div className="credits__notice">
            <p style={{ margin: 0 }}>
              Every trainer avatar in PokeGrove comes from{' '}
              <a href={sourceUrl} target="_blank" rel="noreferrer noopener">
                {sourceName}
              </a>
              . That directory does not share a single licence, and says so:
            </p>
            <blockquote className="credits__quote">{sourceNotice}</blockquote>
            <p className="tiny muted" style={{ marginBottom: 0 }}>
              PokeGrove ships only sprites that the source does not list as custom
              artwork — the Generation I and II game sprites. Nothing has been recoloured,
              cropped, redrawn or rescaled by anything other than a whole number, and every
              sprite is displayed at its original pixel aspect ratio. Anything whose usage
              status is unresolved stays switched off rather than shown.
            </p>
          </div>

          {byArtist.length > 0 && (
            <div className="stack-sm">
              <div className="tiny mono muted">CREDITED ARTISTS</div>
              {byArtist.map((row) => (
                <div key={row.artist} className="stack-sm">
                  <strong>{row.artist}</strong>
                  <p className="tiny muted" style={{ margin: 0 }}>
                    {row.sprites.join(' · ')}
                  </p>
                </div>
              ))}
            </div>
          )}

          {unresolved.length > 0 && (
            <div className="stack-sm">
              <div className="tiny mono muted">AWAITING PERMISSION — NOT DISPLAYED IN GAME</div>
              <p className="tiny muted" style={{ margin: 0 }}>
                {unresolved.map((entry) => entry.displayName).join(' · ')}
              </p>
            </div>
          )}

          <div className="stack-sm">
            <div className="row row--between">
              <span className="tiny mono muted">OFFICIALLY SOURCED SPRITES</span>
              <span className="tiny muted">{official.length}</span>
            </div>
            <p className="tiny muted" style={{ margin: 0 }}>
              Generation I and II trainer sprites from the Pokémon games, mirrored from{' '}
              <a href={sourceUrl} target="_blank" rel="noreferrer noopener">
                {sourceName}
              </a>
              . Pokémon and all related characters are trademarks of Nintendo, Game Freak
              and The Pokémon Company. PokeGrove is not affiliated with any of them.
            </p>

            <div className="credits__grid">
              {official.map((entry) => (
                <div key={entry.filename} className="credits__row">
                  <span className="tavatar">
                    <img
                      className="pixel tavatar__img"
                      src={`/trainers/${entry.filename}`}
                      alt=""
                      width={48}
                      height={48}
                      loading="lazy"
                      decoding="async"
                    />
                  </span>
                  <span className="credits__meta">
                    <span className="tiny">{entry.displayName}</span>
                    <a
                      className="tiny muted mono credits__file"
                      href={entry.sourceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {entry.filename}
                    </a>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      <Panel title="Everything else">
        <div className="stack-sm">
          <p className="tiny muted" style={{ margin: 0 }}>
            Pokémon sprites and species data come from{' '}
            <a href="https://pokeapi.co/" target="_blank" rel="noreferrer noopener">
              PokéAPI
            </a>
            . Every other piece of art in PokeGrove — the frames, the badges, the ribbons,
            the icons, the Groves — is drawn in CSS and SVG by this project.
          </p>
        </div>
      </Panel>
    </div>
  );
}
